import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import { CalendarDays, Clock3, Infinity as InfinityIcon, Zap, ShieldCheck } from "lucide-react";

const STATS = [
  {
    icon: CalendarDays,
    prefix: "",
    to: 28,
    suffix: "-Day",
    label: "Immersive inpatient program",
    sub: "Medical detox · therapy · daily rhythm",
  },
  {
    icon: Clock3,
    prefix: "",
    to: 24,
    suffix: "/7",
    label: "On-call medical & care staff",
    sub: "Counsellors, doctors, real humans — on call for you",
  },
  {
    icon: InfinityIcon,
    prefix: "",
    to: null,
    text: "Lifetime",
    suffix: "",
    label: "Aftercare & alumni support",
    sub: "You keep the community, forever",
  },
  {
    icon: Zap,
    prefix: "",
    to: null,
    text: "Same-day",
    suffix: "",
    label: "Immediate admittance",
    sub: "Shorter wait lists · Private-pay & GreenShield",
  },
];

/* Number that counts up from 0 → target when scrolled into view */
const Counter = ({ to, duration = 1.4 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView || to == null) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return <span ref={ref}>{display}</span>;
};

const StatCard = ({ s, i }) => {
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { once: true, margin: "-10% 0px" });
  // Tilt-on-cursor micro-interaction
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 15 });
  const onMove = (e) => {
    const r = cardRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const Icon = s.icon;
  return (
    <motion.div
      ref={cardRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
      style={{ perspective: 800 }}
      className="relative group"
      data-testid={`trust-stat-${i}`}
    >
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative rounded-2xl border border-white/12 bg-white/[0.05] backdrop-blur-sm p-6 md:p-7 h-full overflow-hidden transition-colors group-hover:border-terracotta/40"
      >
        {/* Corner accent line */}
        <span className="absolute top-0 left-0 h-px w-0 bg-terracotta transition-[width] duration-500 group-hover:w-full" />
        <span className="absolute top-0 left-0 w-px h-0 bg-terracotta transition-[height] duration-500 group-hover:h-full" />

        {/* Icon in a soft disc */}
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-terracotta/15 text-terracotta border border-terracotta/25 transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
            <Icon className="w-5 h-5" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">
            {String(i + 1).padStart(2, "0")}
          </span>
        </div>

        {/* Big value */}
        <div className="mt-6 flex items-baseline gap-1">
          {s.to != null ? (
            <span className="font-serif text-6xl md:text-7xl leading-none text-white tabular-nums tracking-tight">
              <Counter to={s.to} />
            </span>
          ) : (
            <motion.span
              initial={{ opacity: 0, letterSpacing: "0.1em" }}
              animate={inView ? { opacity: 1, letterSpacing: "-0.02em" } : {}}
              transition={{ duration: 0.8, delay: 0.15 + i * 0.1 }}
              className="font-serif text-5xl md:text-6xl leading-none text-white tracking-tight"
            >
              {s.text}
            </motion.span>
          )}
          <span className="font-serif text-2xl md:text-3xl text-terracotta">{s.suffix}</span>
        </div>

        {/* Label + sub */}
        <p className="mt-4 text-white text-sm md:text-base font-semibold leading-snug">
          {s.label}
        </p>
        <p className="mt-1 text-white/60 text-xs md:text-[13px] leading-snug">{s.sub}</p>

        {/* Soft shimmer sweep on hover */}
        <span className="pointer-events-none absolute -inset-x-full inset-y-0 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent skew-x-12 group-hover:animate-[shine_1.1s_ease-out] " />
      </motion.div>
    </motion.div>
  );
};

const TrustBar = () => (
  <section className="relative bg-forestdark text-sand overflow-hidden">
    {/* Animated grid backdrop */}
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }}
      aria-hidden
    />
    {/* Floating soft blobs */}
    <motion.div
      aria-hidden
      className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-terracotta/15 blur-3xl"
      animate={{ x: [0, 40, 0], y: [0, 20, 0] }}
      transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      aria-hidden
      className="absolute -bottom-24 -right-24 w-[520px] h-[520px] rounded-full bg-sage/20 blur-3xl"
      animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
      transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
    />

    <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-20">
      {/* Section header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-semibold flex items-center gap-2"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Care by the numbers
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-3 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-3xl"
          >
            Real care, held together by a schedule you can trust.
          </motion.h2>
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="text-sand/70 text-sm md:text-base max-w-sm md:text-right"
        >
          A residential home. A licensed medical team. A community waiting for you on the other side.
        </motion.p>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {STATS.map((s, i) => (
          <StatCard key={s.label} s={s} i={i} />
        ))}
      </div>
    </div>
  </section>
);

export default TrustBar;
